const IFRAME_API_URL = 'https://www.youtube.com/iframe_api';

export interface YouTubePlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
}

interface YouTubePlayerEvents {
  onReady?: (event: { target: YouTubePlayer }) => void;
  onStateChange?: (event: { target: YouTubePlayer; data: number }) => void;
}

/** The slice of YouTube's IFrame Player API (`window.YT`) KUI uses. */
export interface YouTubeIframeApi {
  Player: new (element: HTMLIFrameElement, options: { events: YouTubePlayerEvents }) => YouTubePlayer;
  PlayerState: { ENDED: number };
}

/**
 * The page globals the IFrame API reads and writes. Kept off the global `Window` type because a host
 * may declare them differently (Phoenix's @nuxt/scripts does).
 */
export interface YouTubeGlobals {
  YT?: Partial<YouTubeIframeApi>;
  onYouTubeIframeAPIReady?: () => void;
}

let api: Promise<YouTubeIframeApi> | null = null;

/**
 * Loads YouTube's IFrame Player API once per page. The player controls an embed whose src
 * carries `enablejsapi=1`. Rejects, and lets a later call try again, when the script is blocked.
 */
export const loadYouTubeIframeApi = (): Promise<YouTubeIframeApi> => {
  const page = window as unknown as YouTubeGlobals;
  api ??= new Promise((resolve, reject) => {
    if (page.YT?.Player) {
      resolve(page.YT as YouTubeIframeApi);
      return;
    }
    const previousReady = page.onYouTubeIframeAPIReady;
    page.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve(page.YT as YouTubeIframeApi);
    };
    const script = document.createElement('script');
    script.src = IFRAME_API_URL;
    script.async = true;
    script.onerror = () => {
      api = null;
      page.onYouTubeIframeAPIReady = previousReady;
      script.remove();
      reject(new Error('The YouTube IFrame Player API failed to load'));
    };
    document.head.append(script);
  });
  return api;
};
